import { useEffect, useCallback } from "react"

import { Menu } from 'lucide-react'
import { MessageList } from "@chatscope/chat-ui-kit-react"

import { Message as HAMessage } from '../../../../shared-src/message'
import { PaginatedMessagesResponse } from '../../../../shared-src/paginated-messages'
import { MessageService } from '../../../../shared-src/message-service'

import { useChatStore } from '../../state/chat-store'
import { getFeathersClient } from "./feathers-client"
import { useAppVisibility } from '@/state/use-app-visibility'
import { fetchMessages, fetchChannelDetails } from '@/state/chat-actions'

import { Message } from "./message"
import { getColorSet } from "@/theme/color"
import { useUserStore } from "@/state/user-store"
import { useSessionContext } from "@/state/session-context"
import { MessageInput2 } from "./message-input-2"
import { DmStatusBanner } from "./dm-status-banner"
import { Channel, ChannelType } from "../../../../shared-src/channel"

interface MessagesAreaProps {
	onToggleChannels: () => void
	onToggleDetails: () => void
}

// Helper text shown in the canvas of an empty channel, so a new user landing on
// an unpopulated channel sees a hint rather than a blank space. Returning
// undefined means "don't show any helper text" — used for channel types that
// already have other affordances (DM status banner, announcement read-only label)
// or where the empty state is unusual (bot channel, support).
function getEmptyChannelHelperText(channel: Channel | null): string | undefined {
	return undefined
	// if (!channel) return undefined
	// switch (channel.type) {
	// 	case ChannelType.public:
	// 		return "This channel is open to everyone. Say hi!"
	// 	case ChannelType.hangaroundGroup:
	// 	case ChannelType.userGroup:
	// 		return "Your group is brand new. Wait for ChumBot's kickoff message, or start the conversation."
	// 	default:
	// 		return undefined
	// }
}

export function MessagesArea(props: MessagesAreaProps) {
	const activeChannelId = useChatStore(state => state.activeChannelId)

	const user = useUserStore(state => state.user)
	const { session } = useSessionContext()

	const messagesByChannelId = useChatStore(state => state.messagesByChannelId)
	const prependMessages = useChatStore(state => state.prependMessages)
	const loadingMoreByChannelId = useChatStore(state => state.loadingMoreByChannelId)
	const hasMoreMessagesByChannelId = useChatStore(state => state.hasMoreMessagesByChannelId)
	const setLoadingMore = useChatStore(state => state.setLoadingMore)
	const pageSize = useChatStore(state => state.pageSize)
	const activeChannel = useChatStore(state => state.activeChannel)
	const invalidateInactiveChannelsMessages = useChatStore(state => state.invalidateInactiveChannelsMessages)

	const feathersClient = getFeathersClient()
	const messagesService = feathersClient.service('messages') as unknown as MessageService

	const loadMoreMessages = useCallback(async (channelId: string) => {
		const currentMessages = messagesByChannelId[channelId] || []
		const beforeUlid = currentMessages.length > 0 ?
			currentMessages[0].ulid :
			undefined

		setLoadingMore(channelId, true)
		try {
			//	Probe logging for ha-287 (Load-more silently returning empty).
			//	Cheap way to catch service-worker-staleness or socket-race regressions
			//	in the field without a full repro; scenario step 15 stays the primary sentinel.
			console.log('[loadMoreMessages] request', { channelId, beforeUlid, maxCount: pageSize })
			const response: PaginatedMessagesResponse = await messagesService.loadPage({
				channelId,
				maxCount: pageSize,
				beforeUlid: beforeUlid
			})
			console.log('[loadMoreMessages] response', { messagesCount: response.messages.length, hasMore: response.hasMore })
			prependMessages(channelId, {
				messages: response.messages,
				hasMore: response.hasMore
			})
		} catch (error) {
			console.error('Failed to load more messages:', error)
		} finally {
			setLoadingMore(channelId, false)
		}
	}, [messagesByChannelId, messagesService, prependMessages, setLoadingMore, pageSize])

	const handleFetchMessages = useCallback(async () => {
		if (!activeChannelId)
			return

		await fetchMessages({ channelId: activeChannelId })
	}, [activeChannelId])

	const handleFetchChannelDetails = useCallback(async () => {
		if (!activeChannelId)
			return

		await fetchChannelDetails({ channelId: activeChannelId })
	}, [activeChannelId])

	useEffect(() => {
		if (!activeChannelId)
			return

		const existingMessages = messagesByChannelId[activeChannelId] || []
		const shouldFetchMessages = existingMessages.length < pageSize

		if (shouldFetchMessages)
			handleFetchMessages()

		handleFetchChannelDetails()

		// disable eslint because messagesByChannelId is intentionally omitted to avoid infinite loop.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeChannelId, pageSize, handleFetchMessages, handleFetchChannelDetails])

	const handleForeground = useCallback(() => {
		if (!activeChannelId)
			return

		invalidateInactiveChannelsMessages(activeChannelId)
		handleFetchMessages()
		handleFetchChannelDetails()
	}, [activeChannelId, invalidateInactiveChannelsMessages, handleFetchMessages, handleFetchChannelDetails])

	useAppVisibility({
		onForeground: handleForeground,
		enabled: !!activeChannelId
	})
	
	if (!activeChannelId) return null

	const renderMessages = () => {
		const messageComponents: React.ReactNode[] = []
		const messages: HAMessage[] | undefined = messagesByChannelId[activeChannelId]
		// `messages === undefined` means we haven't fetched yet — render nothing to avoid
		// flashing the empty-state helper before the first page arrives.
		if (!messages)
			return messageComponents
		if (messages.length === 0) {
			const helperText = getEmptyChannelHelperText(activeChannel)
			if (!helperText)
				return messageComponents
			messageComponents.push(
				<div
					key="empty-state-helper"
					className="px-6 py-12 text-center text-sm text-gray-500"
				>
					{helperText}
				</div>
			)
			return messageComponents
		}

		let previousSenderId: string | undefined = undefined
		const lastMessageUlid = messages[messages.length - 1].ulid
		for (const message of messages) {
			const isLastMessage = message.ulid === lastMessageUlid
			messageComponents.push(
				<Message
					key={message.ulid}
					message={message}
					selfUser={user}
					showSender={previousSenderId !== message.senderId}
					isLastMessage={isLastMessage}
				/>
			)
			previousSenderId = message.senderId
		}
		return messageComponents
	}

	const colorSet = getColorSet({ senderColor: user?.color })

	if (!activeChannel)
		return null

	const channelType = ChannelType.fromChannelId(activeChannel.id)
	const isDmChannel = channelType === ChannelType.dm
	const showDmBanner = isDmChannel && activeChannel.dmStatus && (
		activeChannel.dmStatus.myStatus === 'pending' ||
		(activeChannel.dmStatus.myStatus === 'accepted' && activeChannel.dmStatus.theirStatus === 'pending')
	)

	return (
		<>
			<div className="px-4 py-4 border-b flex items-center gap-4"
				style={{ borderColor: colorSet.border }}>
				<button onClick={props.onToggleChannels} className="md:hidden p-2">
					<Menu className="h-5 w-5" color={colorSet.dark}/>
				</button>
				<div className="flex flex-col gap-1 flex-1">
					<p className="text-lg font-semibold" style={{ color: colorSet.dark }}>
						{activeChannel?.name}
					</p>
					{/* <p className="text-sm">
						{activeChannel?.id}
					</p> */}
				</div>
				<button onClick={props.onToggleDetails} className="p-2">
					<Menu className="h-5 w-5" color={colorSet.dark}/>
				</button>
			</div>

			{showDmBanner && <DmStatusBanner channel={activeChannel} />}

			{/*	ha-203: heavy inset shadow on the message stream when the logged-in admin/dev
				is impersonating. matches the orange banner in channels-header for visual
				consistency, and keeps the warning in the user's primary focus area. the shadow
				is drawn by a pointer-events:none overlay sibling because the chat-ui-kit
				MessageList has its own opaque background that would otherwise cover an inset
				shadow on the scrolling parent. */}
			<div className="flex-1 relative min-h-0">
				{session?.isImpersonating && (
					<div
						className="absolute inset-0 pointer-events-none z-10"
						style={{ boxShadow: 'inset 0 0 0 4px #b45309' }}
						data-testid="impersonation-stream-shadow"
					/>
				)}
				<div className="absolute inset-0 overflow-y-auto scroll-smooth">
					<MessageList>
						<MessageList.Content>
							{activeChannelId && hasMoreMessagesByChannelId[activeChannelId] && (
								<div className="px-4 py-2">
									<button
										onClick={() => loadMoreMessages(activeChannelId)}
										disabled={loadingMoreByChannelId[activeChannelId]}
										className="w-full py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition-colors"
									>
										{loadingMoreByChannelId[activeChannelId] ? "Loading..." : "Load more messages"}
									</button>
								</div>
							)}
							{renderMessages()}
						</MessageList.Content>
					</MessageList>
				</div>
			</div>

			<div className="border-t p-4" style={{ borderColor: colorSet.border }}>
				<div className="flex gap-2 items-center">
					<div className="flex-1">
						<MessageInput2 />
					</div>
				</div>
			</div>
		</>
	)
} 